/**
 * SAGA Monitoring Dashboard - Life-Critical Visualization
 *
 * Provides real-time monitoring and audit trail visualization for SAGA
 * coordination across all tenants. ALL data comes from tenant configuration
 * and maintains domain-agnostic, data-driven architecture.
 *
 * LIFE-CRITICAL: This dashboard monitors patient safety, financial integrity,
 * and legal compliance workflows across healthcare, financial, and legal domains.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  Group,
  Text,
  Badge,
  Grid,
  Stack,
  ActionIcon,
  Alert,
  Tabs,
  Table,
  Progress,
  Timeline,
  ThemeIcon,
  Tooltip,
  Select,
  TextInput,
  Button,
  Modal,
  JsonInput,
  ScrollArea,
  Container,
  Title
} from '@mantine/core';
import {
  IconActivity,
  IconAlertTriangle,
  IconCircleCheck,
  IconClock,
  IconDatabase,
  IconExclamationMark,
  IconEye,
  IconFilter,
  IconRefresh,
  IconSearch,
  IconShield,
  IconTimeline,
  IconUsers,
  IconX
} from '@tabler/icons-react';
import { useQuery, useMutation, useSubscription } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { notifications } from '@mantine/notifications';
import { useCurrentTenant } from '../hooks/useCurrentTenant';
import { useAuditLogger } from '../hooks/useAuditLogger';
import { LoadingOverlay } from '../components/LoadingOverlay';
import { ErrorBoundary } from '../error/ErrorBoundary';

// Status constants (matches generated GraphQL enums)
const Status = {
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  COMPENSATED: 'COMPENSATED',
} as const;

// GraphQL Queries (data-driven from tenant configuration)
// Note: Using DAG API as SAGA executions are handled through DAG workflow engine
const GET_SAGA_EXECUTIONS = gql`
  query GetSagaExecutions($limit: Int) {
    dagExecutions(limit: $limit) {
      executionId
      workflowName
      status
      startedAt
      durationMs
      success
    }
  }
`;

const GET_SAGA_EXECUTION_DETAILS = gql`
  query GetSagaExecutionDetails($executionId: ID!) {
    dagExecutionStatus(executionId: $executionId) {
      executionId
      status
      startedAt
      completedAt
      totalNodes
      completedNodes
      failedNodes
      activeNodes
    }
  }
`;

// TODO: SAGA definitions query not yet implemented in backend DAG API
// Using stub data for now - will be implemented when backend supports workflow definitions query
const GET_SAGA_DEFINITIONS = gql`
  query GetSagaDefinitions {
    __typename
  }
`;

const GET_SAGA_SAFETY_SCORES = gql`
  query GetSagaSafetyScores($tenantId: String!, $executionId: String!) {
    sagaSafetyScores(tenantId: $tenantId, executionId: $executionId) {
      assessmentId
      executionId
      overall
      domains {
        healthcare
        financial
        legal
        operational
      }
      violations {
        id
        severity
        domain
        violationType
        message
        recommendation
        blockingAction
        createdAt
      }
      timestamp
      metadata
    }
  }
`;

const SUBSCRIBE_TO_SAGA_SAFETY_EVENTS = gql`
  subscription SagaSafetyEvents($tenantId: String!, $executionId: String!) {
    sagaSafetyEvents(executionId: $executionId) {
      eventType
      assessmentId
      executionId
      safetyScore {
        overall
        domains {
          healthcare
          financial
          legal
          operational
        }
        violations {
          id
          severity
          domain
          violationType
          message
          recommendation
          blockingAction
          createdAt
        }
        timestamp
      }
      metadata
    }
  }
`;

// Subscription available in FormsSubscription (not SafetyMetricsSubscription)
const SUBSCRIBE_TO_SAGA_EXECUTION_EVENTS = gql`
  subscription SagaStateTransitions($tenantId: String!, $sagaExecutionId: String, $primitiveTypes: [String!]) {
    sagaStateTransitions(tenant: $tenantId, sagaExecutionId: $sagaExecutionId, primitiveTypes: $primitiveTypes) {
      eventId
      tenant
      sagaExecutionId
      eventType
      primitiveType
      fromState
      toState
      stepId
      executionResult
      auditTrailId
      timestamp
      userId
      safetyScore
      performanceMetrics
    }
  }
`;

// TODO: Subscription not available in backend
// const SUBSCRIBE_TO_SAGA_STEP_EVENTS = gql`
//   subscription SagaStepEvents($tenantId: String!, $executionId: String!) {
//     sagaStepEvents(executionId: $executionId) {
//       eventType
//       stepId
//       stepName
//       primitiveType
//       status
//       attempts
//       output
//       error
//       duration
//       timestamp
//       metadata
//     }
//   }
// `;
const SUBSCRIBE_TO_SAGA_STEP_EVENTS = gql`
  subscription Placeholder {
    __typename
  }
`;

// TODO: Subscription not available in backend
// const SUBSCRIBE_TO_EMERGENCY_EVENTS = gql`
//   subscription SafetyEventCreated($tenantId: String!, $severityThreshold: String, $categoryFilter: String, $emergencyOnly: Boolean) {
//     safetyEventCreated(tenant: $tenantId, severityThreshold: $severityThreshold, categoryFilter: $categoryFilter, emergencyOnly: $emergencyOnly) {
//       eventId
//       tenant
//       category
//       severity
//       eventType
//       safetyScore
//       safetyScoreDelta
//       remediationRequired
//       escalationTriggered
//       escalationLevel
//       emergencyContactsNotified
//       timestamp
//       userId
//       auditTrailId
//       isStaged
//       description
//     }
//   }
// `;
// Emergency safety events subscription
const SUBSCRIBE_TO_EMERGENCY_EVENTS = gql`
  subscription SafetyEventCreated($tenantId: String!, $categoryFilter: String, $emergencyOnly: Boolean) {
    safetyEventCreated(tenant: $tenantId, categoryFilter: $categoryFilter, emergencyOnly: $emergencyOnly) {
      eventId
      tenant
      category
      severity
      eventType
      safetyScore
      safetyScoreDelta
      remediationRequired
      escalationTriggered
      escalationLevel
      emergencyContactsNotified
      timestamp
      userId
      auditTrailId
      isStaged
      description
    }
  }
`;

// Note: Using saga_state_transitions for coordination monitoring
// Backend doesn't have a separate coordinationEvents subscription
const SUBSCRIBE_TO_COORDINATION_EVENTS = gql`
  subscription SagaStateTransitionsForCoordination($tenantId: String!) {
    sagaStateTransitions(tenant: $tenantId) {
      eventId
      tenant
      sagaExecutionId
      eventType
      primitiveType
      fromState
      toState
      stepId
      executionResult
      auditTrailId
      timestamp
      userId
      safetyScore
      performanceMetrics
    }
  }
`;

const EMERGENCY_HALT_SAGA = gql`
  mutation EmergencyHaltSaga($input: EmergencyHaltSagaInput!) {
    emergencyHaltSaga(input: $input) {
      executionId
      status
      reason
      operator
      haltedAt
      auditContext {
        correlationId
        timestamp
      }
    }
  }
`;

const COMPENSATE_SAGA = gql`
  mutation CompensateSaga($input: CompensateSagaInput!) {
    compensateSaga(input: $input) {
      executionId
      status
      auditContext {
        correlationId
        timestamp
      }
    }
  }
`;

// Type definitions (data-driven from backend)
// Using DAG execution schema as SAGA is handled through DAG workflow engine
interface SagaExecution {
  executionId: string;
  workflowName: string;
  status: string; // DAG execution status enum
  startedAt: string;
  durationMs?: number;
  success: boolean;
}

// Step metadata interface (domain-agnostic, tenant-configurable)
interface SagaStepMetadata {
  retryPolicy?: {
    maxAttempts: number;
    backoffMs: number;
    exponentialBackoff: boolean;
  };
  compensation?: {
    handler: string;
    timeoutSeconds?: number;
  };
  validation?: {
    schema?: Record<string, unknown>;
    rules?: string[];
  };
  safety?: {
    thresholds?: Record<string, number>;
    monitoring?: boolean;
  };
  tenantConfig?: Record<string, unknown>;
  [key: string]: unknown; // Allow additional tenant-specific metadata
}

// SAGA definition metadata interface (data-driven configuration)
interface SagaDefinitionMetadata {
  version: string;
  author?: string;
  description?: string;
  tags?: string[];
  domain?: string;
  businessRules?: Record<string, unknown>;
  complianceFramework?: string[];
  monitoring?: {
    enableMetrics: boolean;
    alertThresholds?: Record<string, number>;
  };
  lifecycle?: {
    createdAt: string;
    updatedAt?: string;
    deprecatedAt?: string;
  };
  tenantOverrides?: Record<string, unknown>;
  [key: string]: unknown; // Allow additional tenant-specific metadata
}

interface SagaDefinition {
  sagaId: string;
  version: string;
  description?: string;
  steps: Array<{
    stepId: string;
    name: string;
    kind: string;
    primitiveType: string; // One of 16 FERP primitive types
    actionHandler: string;
    timeoutSeconds?: number;
    metadata: SagaStepMetadata;
  }>;
  metadata: SagaDefinitionMetadata;
}

interface SagaMetrics {
  totalExecutions: number;
  activeExecutions: number;
  completedExecutions: number;
  failedExecutions: number;
  compensatingExecutions: number;
  averageExecutionTime: number;
  successRate: number;
}

// Status color mapping for life-critical visualization
const getStatusColor = (status: string): string => {
  switch (status) {
    case 'Completed': return 'green';
    case 'InProgress': return 'blue';
    case 'Pending': return 'orange';
    case 'Compensating': return 'yellow';
    case 'Compensated': return 'cyan';
    case 'Failed': return 'red';
    case 'Cancelled': return 'gray';
    default: return 'gray';
  }
};

// Primitive type icons (domain-agnostic visualization)
const getPrimitiveIcon = (primitiveType: string): React.ReactNode => {
  const iconMap: Record<string, React.ReactNode> = {
    wfl: <IconActivity size={16} />,
    ins: <IconDatabase size={16} />,
    sig: <IconShield size={16} />,
    exe: <IconCircleCheck size={16} />,
    mon: <IconEye size={16} />,
    gov: <IconUsers size={16} />,
    pol: <IconShield size={16} />,
    esc: <IconAlertTriangle size={16} />,
    not: <IconExclamationMark size={16} />,
    met: <IconDatabase size={16} />,
    aud: <IconTimeline size={16} />,
    rep: <IconDatabase size={16} />,
    ana: <IconSearch size={16} />,
    opt: <IconActivity size={16} />,
    rel: <IconCircleCheck size={16} />,
    lif: <IconShield size={16} />
  };
  return iconMap[primitiveType] || <IconActivity size={16} />;
};

// Feature flag: Enable subscriptions now that backend RootSubscription properly merges FormsSubscription
// Fixed: Added Default + Clone traits to all subscription types (SafetyMetrics, Forms, Task, Integration, ActorManagement, WorkflowCriticality)
// Fixed: Renamed subscription's SafetyEvent to SafetyEventSubscriptionPayload to avoid GraphQL name collision
const ENABLE_REALTIME_SUBSCRIPTIONS = true;

export const SagaMonitoringDashboard: React.FC = () => {
  const { tenantId: currentTenantId, tenantContext: currentTenant } = useCurrentTenant();
  const { logUserAction } = useAuditLogger();
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [selectedExecution, setSelectedExecution] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshInterval, setRefreshInterval] = useState<number>(5000);
  const [showCompensationModal, setShowCompensationModal] = useState(false);
  const [compensationReason, setCompensationReason] = useState('');

  // Data queries (all data-driven from tenant configuration)
  const {
    data: executionsData,
    loading: executionsLoading,
    error: executionsError,
    refetch: refetchExecutions
  } = useQuery<Record<string, any>>(GET_SAGA_EXECUTIONS, {
    variables: {
      limit: 50
    },
    pollInterval: refreshInterval
  });

  const {
    data: definitionsData,
    loading: definitionsLoading
  } = useQuery<Record<string, any>>(GET_SAGA_DEFINITIONS, {
    skip: true // Skip until backend implements workflow definitions query
  });

  // Execution details query (for future expansion)
  const {
    data: executionDetailsData,
    loading: executionDetailsLoading
  } = useQuery<Record<string, any>>(GET_SAGA_EXECUTION_DETAILS, {
    variables: {
      executionId: selectedExecution
    },
    skip: !selectedExecution
  });

  // LIFE-CRITICAL: Real-time safety monitoring for SAGA operations
  // ✅ Backend resolver implemented in graphql_gateway/src/saga_api.rs
  // Provides tenant-isolated safety scores with domain-specific metrics and violations
  const {
    data: safetyScoresData,
    loading: safetyScoresLoading,
    error: safetyScoresError,
    refetch: refetchSafetyScores
  } = useQuery<Record<string, any>>(GET_SAGA_SAFETY_SCORES, {
    variables: {
      tenantId: currentTenant?.tenantId,
      executionId: selectedExecution
    },
    pollInterval: 2000, // High-frequency polling for safety-critical data
    skip: !currentTenant?.tenantId || !selectedExecution // Enabled: Backend resolver ready
  });

  // COMPREHENSIVE WEBSOCKET SUBSCRIPTIONS - Real-time dashboard updates
  // Subscriptions exist in FormsSubscription but RootSubscription doesn't expose them yet
  // Using feature flag to completely bypass subscription hooks until backend is fixed

  // Global SAGA execution events for the tenant
  const { data: executionEventsData } = useSubscription<Record<string, any>>(SUBSCRIBE_TO_SAGA_EXECUTION_EVENTS, {
    variables: {
      tenantId: currentTenant?.tenantId,
      sagaExecutionId: undefined,
      primitiveTypes: undefined
    },
    skip: !ENABLE_REALTIME_SUBSCRIPTIONS || !currentTenant?.tenantId,
    onData: ({ data }) => {
      if (data?.data?.sagaStateTransitions) {
        // Auto-refresh executions when new events arrive
        refetchExecutions();

        // Show real-time notifications for critical events
        const event = data.data?.sagaStateTransitions;
        const status = event.toState; // State transition target
        if (status === 'failed' || status === 'compensated') {
          notifications.show({
            title: `SAGA ${status.toUpperCase()}`,
            message: `Execution ${event.sagaExecutionId}`,
            color: status === 'failed' ? 'red' : 'orange'
          });
        }
      }
    }
  });

  // Step-level events for selected execution
  const { data: stepEventsData } = useSubscription<Record<string, any>>(SUBSCRIBE_TO_SAGA_STEP_EVENTS, {
    variables: {
      tenantId: currentTenant?.tenantId,
      executionId: selectedExecution
    },
    skip: !ENABLE_REALTIME_SUBSCRIPTIONS || !currentTenant?.tenantId || !selectedExecution,
    onData: ({ data }) => {
      if (data?.data?.sagaStepEvents) {
        const event = data.data?.sagaStepEvents;

        // Show step completion notifications
        if (event.status === Status.COMPLETED) {
          notifications.show({
            title: 'Step Completed',
            message: `${event.stepName} (${event.primitiveType})`,
            color: 'green',
            autoClose: 3000
          });
        } else if (event.status === Status.FAILED) {
          notifications.show({
            title: 'Step Failed',
            message: `${event.stepName}: ${event.error}`,
            color: 'red',
            autoClose: false
          });
        }
      }
    }
  });

  // Emergency events (life-critical notifications)
  const { data: emergencyEventsData } = useSubscription<Record<string, any>>(SUBSCRIBE_TO_EMERGENCY_EVENTS, {
    variables: {
      tenantId: currentTenant?.tenantId,
      severityThreshold: 'Critical',
      categoryFilter: undefined,
      emergencyOnly: true
    },
    skip: !ENABLE_REALTIME_SUBSCRIPTIONS || !currentTenant?.tenantId,
    onData: ({ data }) => {
      if (data?.data?.safetyEventCreated) {
        const event = data.data?.safetyEventCreated;

        // Critical emergency notifications
        notifications.show({
          title: '🚨 EMERGENCY EVENT',
          message: `${event.eventType}: ${event.description}`,
          color: 'red',
          autoClose: false,
          withCloseButton: true
        });

        // Auto-refresh all data on emergency events
        refetchExecutions();
        // refetchSafetyScores(); // DISABLED: Backend resolver not implemented yet
      }
    }
  });

  // Coordination events (distributed system monitoring)
  const { data: coordinationEventsData } = useSubscription<Record<string, any>>(SUBSCRIBE_TO_COORDINATION_EVENTS, {
    variables: { tenantId: currentTenant?.tenantId },
    skip: !currentTenant?.tenantId,
    onData: ({ data }) => {
      if (data?.data?.sagaStateTransitions) {
        const event = data.data?.sagaStateTransitions;

        // Show coordination alerts for state transition issues
        if (event.fromState === 'failed' || event.toState === 'failed') {
          notifications.show({
            title: 'Coordination Issue',
            message: `State transition failure for execution ${event.sagaExecutionId}`,
            color: 'yellow',
            autoClose: 5000
          });
        }
      }
    }
  });

  const [compensateSaga] = useMutation<Record<string, any>>(COMPENSATE_SAGA);
  const [emergencyHaltSaga] = useMutation<Record<string, any>>(EMERGENCY_HALT_SAGA);

  // Auto-refresh controls
  useEffect(() => {
    const interval = setInterval(() => {
      if (refreshInterval > 0) {
        refetchExecutions();
      }
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [refreshInterval, refetchExecutions]);

  // Calculate metrics (data-driven from actual executions)
  // Using DAG execution schema
  const metrics = useMemo((): SagaMetrics => {
    if (!executionsData?.dagExecutions) {
      return {
        totalExecutions: 0,
        activeExecutions: 0,
        completedExecutions: 0,
        failedExecutions: 0,
        compensatingExecutions: 0,
        averageExecutionTime: 0,
        successRate: 0
      };
    }

    const executions = executionsData.dagExecutions;
    const total = executions.length;
    const active = executions.filter((e: SagaExecution) => e.status === 'Running').length;
    const completed = executions.filter((e: SagaExecution) => e.success === true).length;
    const failed = executions.filter((e: SagaExecution) => e.success === false).length;
    const compensating = 0; // DAG doesn't have compensating state

    const completedWithTime = executions.filter((e: SagaExecution) =>
      e.success === true && e.durationMs
    );

    const avgTime = completedWithTime.length > 0
      ? completedWithTime.reduce((sum: number, e: SagaExecution) => {
          return sum + (e.durationMs! / 1000); // Convert ms to seconds
        }, 0) / completedWithTime.length
      : 0;

    const successRate = total > 0 ? (completed / total) * 100 : 0;

    return {
      totalExecutions: total,
      activeExecutions: active,
      completedExecutions: completed,
      failedExecutions: failed,
      compensatingExecutions: compensating,
      averageExecutionTime: avgTime,
      successRate
    };
  }, [executionsData]);

  // Filter executions based on search and status
  const filteredExecutions = useMemo(() => {
    if (!executionsData?.dagExecutions) return [];

    return executionsData.dagExecutions.filter((execution: SagaExecution) => {
      const matchesSearch = searchTerm === '' ||
        execution.workflowName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        execution.executionId.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'all' || execution.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [executionsData, searchTerm, statusFilter]);

  // Handle compensation action (life-critical operation)
  const handleCompensation = useCallback(async () => {
    if (!selectedExecution || !compensationReason.trim()) {
      notifications.show({
        title: 'Compensation Failed',
        message: 'Please provide a compensation reason',
        color: 'red'
      });
      return;
    }

    try {
      // Log life-critical action
      await logUserAction({
        action: 'saga_compensation_initiated',
        details: {
          targetId: selectedExecution,
          reason: compensationReason
        },
        metadata: {
          tenantId: currentTenant?.tenantId
        }
      });

      const result = await compensateSaga({
        variables: {
          input: {
            executionId: selectedExecution,
            compensationReason,
            compensationContext: {
              initiatedBy: 'dashboard_user',
              timestamp: new Date().toISOString()
            }
          }
        }
      });

      notifications.show({
        title: 'LIFE-CRITICAL: Compensation Initiated',
        message: `SAGA ${selectedExecution} compensation started`,
        color: 'orange'
      });

      setShowCompensationModal(false);
      setCompensationReason('');
      refetchExecutions();

    } catch (error) {
      console.error('Compensation failed:', error);
      notifications.show({
        title: 'LIFE-CRITICAL: Compensation Failed',
        message: 'Failed to initiate SAGA compensation',
        color: 'red'
      });
    }
  }, [selectedExecution, compensationReason, compensateSaga, logUserAction, currentTenant?.tenantId, refetchExecutions]);

  // Render overview metrics
  const renderOverview = () => (
    <Grid>
      <Grid.Col span={4}>
        <Card>
          <Group justify="space-between">
            <div>
              <Text size="sm" color="dimmed">Total Executions</Text>
              <Text size="xl" fw={700}>{metrics.totalExecutions}</Text>
            </div>
            <ThemeIcon size="lg" color="blue">
              <IconActivity size={20} />
            </ThemeIcon>
          </Group>
        </Card>
      </Grid.Col>

      <Grid.Col span={4}>
        <Card>
          <Group justify="space-between">
            <div>
              <Text size="sm" color="dimmed">Active Executions</Text>
              <Text size="xl" fw={700} color="blue">{metrics.activeExecutions}</Text>
            </div>
            <ThemeIcon size="lg" color="blue">
              <IconClock size={20} />
            </ThemeIcon>
          </Group>
        </Card>
      </Grid.Col>

      <Grid.Col span={4}>
        <Card>
          <Group justify="space-between">
            <div>
              <Text size="sm" color="dimmed">Success Rate</Text>
              <Text size="xl" fw={700} color="green">
                {metrics.successRate.toFixed(1)}%
              </Text>
            </div>
            <ThemeIcon size="lg" color="green">
              <IconCircleCheck size={20} />
            </ThemeIcon>
          </Group>
        </Card>
      </Grid.Col>

      <Grid.Col span={6}>
        <Card>
          <Text size="sm" fw={500} mb="md">Execution Status Distribution</Text>
          <Stack gap="xs">
            <Group justify="space-between">
              <Text size="sm">Completed</Text>
              <Badge color="green">{metrics.completedExecutions}</Badge>
            </Group>
            <Group justify="space-between">
              <Text size="sm">In Progress</Text>
              <Badge color="blue">{metrics.activeExecutions}</Badge>
            </Group>
            <Group justify="space-between">
              <Text size="sm">Failed</Text>
              <Badge color="red">{metrics.failedExecutions}</Badge>
            </Group>
            <Group justify="space-between">
              <Text size="sm">Compensating</Text>
              <Badge color="yellow">{metrics.compensatingExecutions}</Badge>
            </Group>
          </Stack>
        </Card>
      </Grid.Col>

      <Grid.Col span={6}>
        <Card>
          <Text size="sm" fw={500} mb="md">Performance Metrics</Text>
          <Stack gap="xs">
            <Group justify="space-between">
              <Text size="sm">Average Execution Time</Text>
              <Text size="sm" fw={500}>
                {metrics.averageExecutionTime.toFixed(1)}s
              </Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm">Success Rate</Text>
              <Progress
                value={metrics.successRate}
                color={metrics.successRate > 95 ? 'green' : metrics.successRate > 80 ? 'yellow' : 'red'}
                size="sm"
                radius="xl"
              />
            </Group>
          </Stack>
        </Card>
      </Grid.Col>
    </Grid>
  );

  // Render execution list
  const renderExecutions = () => (
    <Stack>
      <Group justify="space-between">
        <Group>
          <TextInput
            placeholder="Search executions..."
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            leftSection={<IconSearch size={16} />}
          />
          <Select
            data={[
              { value: 'all', label: 'All Status' },
              { value: 'Pending', label: 'Pending' },
              { value: 'InProgress', label: 'In Progress' },
              { value: 'Completed', label: 'Completed' },
              { value: 'Failed', label: 'Failed' },
              { value: 'Compensating', label: 'Compensating' },
              { value: 'Cancelled', label: 'Cancelled' }
            ]}
            value={statusFilter}
            onChange={(value: string | null) => setStatusFilter(value || 'all')}
            leftSection={<IconFilter size={16} />}
          />
        </Group>
        <Group>
          <Select
            data={[
              { value: '0', label: 'No Auto-refresh' },
              { value: '5000', label: 'Every 5s' },
              { value: '10000', label: 'Every 10s' },
              { value: '30000', label: 'Every 30s' }
            ]}
            value={refreshInterval.toString()}
            onChange={(value: string | null) => setRefreshInterval(parseInt(value || '0'))}
          />
          <ActionIcon onClick={() => refetchExecutions()}>
            <IconRefresh size={16} />
          </ActionIcon>
        </Group>
      </Group>

      <ScrollArea h={600}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Execution ID</Table.Th>
              <Table.Th>SAGA ID</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Current State</Table.Th>
              <Table.Th>Initiator</Table.Th>
              <Table.Th>Started At</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filteredExecutions.map((execution: SagaExecution) => (
              <Table.Tr key={execution.executionId}>
                <Table.Td>
                  <Text size="sm" ff="monospace">
                    {execution.executionId.slice(0, 8)}...
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" fw={500}>{execution.workflowName}</Text>
                </Table.Td>
                <Table.Td>
                  <Badge color={getStatusColor(execution.status)} size="sm">
                    {execution.status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{execution.success ? 'Success' : 'Failed'}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">System</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">
                    {new Date(execution.startedAt).toLocaleString()}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <ActionIcon
                      size="sm"
                      onClick={() => setSelectedExecution(execution.executionId)}
                    >
                      <IconEye size={14} />
                    </ActionIcon>
                    {execution.status === 'Running' && (
                      <ActionIcon
                        size="sm"
                        color="orange"
                        onClick={() => {
                          setSelectedExecution(execution.executionId);
                          setShowCompensationModal(true);
                        }}
                      >
                        <IconX size={14} />
                      </ActionIcon>
                    )}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Stack>
  );

  // Render SAGA definitions (data-driven from tenant config)
  const renderDefinitions = () => (
    <ScrollArea h={600}>
      <Stack>
        {definitionsData?.sagaDefinitions?.map((definition: SagaDefinition) => (
          <Card key={definition.sagaId}>
            <Group justify="space-between" mb="md">
              <div>
                <Text fw={500}>{definition.sagaId}</Text>
                <Text size="sm" color="dimmed">Version {definition.version}</Text>
                {definition.description && (
                  <Text size="sm" mt="xs">{definition.description}</Text>
                )}
              </div>
              <Badge>{definition.steps.length} steps</Badge>
            </Group>

            <Text size="sm" fw={500} mb="xs">Workflow Steps:</Text>
            <Timeline>
              {definition.steps.map((step, index) => (
                <Timeline.Item
                  key={step.stepId}
                  bullet={getPrimitiveIcon(step.primitiveType)}
                  title={step.name}
                >
                  <Group gap="xs">
                    <Badge size="xs" color="blue">{step.primitiveType}</Badge>
                    <Badge size="xs" color="gray">{step.kind}</Badge>
                    {step.timeoutSeconds && (
                      <Badge size="xs" color="orange">
                        {step.timeoutSeconds}s timeout
                      </Badge>
                    )}
                  </Group>
                  <Text size="xs" color="dimmed" mt="xs">
                    Handler: {step.actionHandler}
                  </Text>
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>
        ))}
      </Stack>
    </ScrollArea>
  );

  // LIFE-CRITICAL: Real-time safety monitoring for selected SAGA execution
  const renderSafetyMonitoring = () => {
    if (!selectedExecution) {
      return (
        <Alert color="blue" icon={<IconShield />}>
          Select a SAGA execution to view real-time safety monitoring
        </Alert>
      );
    }

    if (safetyScoresLoading) {
      return <LoadingOverlay />;
    }

    if (safetyScoresError) {
      return (
        <Alert color="red" icon={<IconAlertTriangle />}>
          Failed to load safety scores: {safetyScoresError.message}
        </Alert>
      );
    }

    const safetyData = safetyScoresData?.sagaSafetyScores;
    const overallScore = safetyData?.overall || 0;
    const isCritical = overallScore < 70;

    return (
      <Stack>
        {/* Overall Safety Score */}
        <Card>
          <Group justify="space-between" mb="md">
            <Title order={4}>Overall Safety Score</Title>
            <Group gap="xs">
              <Badge
                color={isCritical ? 'red' : overallScore < 85 ? 'yellow' : 'green'}
                size="lg"
              >
                {overallScore.toFixed(1)}
              </Badge>
              {isCritical && (
                <Button
                  color="red"
                  size="sm"
                  leftSection={<IconExclamationMark size={16} />}
                  onClick={() => handleEmergencyHalt()}
                >
                  Emergency Halt
                </Button>
              )}
            </Group>
          </Group>

          <Progress
            value={overallScore}
            color={isCritical ? 'red' : overallScore < 85 ? 'yellow' : 'green'}
            size="lg"
            mb="md"
          />

          {isCritical && (
            <Alert color="red" icon={<IconAlertTriangle />} mb="md">
              <Text fw={500}>CRITICAL SAFETY VIOLATION</Text>
              <Text size="sm">
                Safety score below 70 threshold. SAGA execution may be automatically halted.
              </Text>
            </Alert>
          )}
        </Card>

        {/* Domain-Specific Safety Scores */}
        <Card>
          <Title order={4} mb="md">Domain Safety Scores</Title>
          <Grid>
            {safetyData?.domains && Object.entries(safetyData.domains).map(([domain, score]) => (
              <Grid.Col span={6} key={domain}>
                <Card withBorder>
                  <Group justify="space-between" mb="xs">
                    <Text fw={500} tt="capitalize">{domain}</Text>
                    <Badge
                      color={typeof score === 'number' && score < 70 ? 'red' : typeof score === 'number' && score < 85 ? 'yellow' : 'green'}
                    >
                      {typeof score === 'number' ? score.toFixed(1) : 'N/A'}
                    </Badge>
                  </Group>
                  <Progress
                    value={typeof score === 'number' ? score : 0}
                    color={typeof score === 'number' && score < 70 ? 'red' : typeof score === 'number' && score < 85 ? 'yellow' : 'green'}
                    size="sm"
                  />
                </Card>
              </Grid.Col>
            ))}
          </Grid>
        </Card>

        {/* Safety Violations */}
        {safetyData?.violations?.length > 0 && (
          <Card>
            <Title order={4} mb="md">Safety Violations</Title>
            <Stack>
              {safetyData.violations.map((violation: any) => (
                <Alert
                  key={violation.id}
                  color={violation.severity === 'CRITICAL' ? 'red' :
                         violation.severity === 'HIGH' ? 'orange' : 'yellow'}
                  icon={<IconAlertTriangle />}
                >
                  <Group justify="space-between" mb="xs">
                    <Text fw={500}>{violation.violationType}</Text>
                    <Badge color={violation.severity === 'CRITICAL' ? 'red' :
                                  violation.severity === 'HIGH' ? 'orange' : 'yellow'}>
                      {violation.severity}
                    </Badge>
                  </Group>
                  <Text size="sm" mb="xs">{violation.message}</Text>
                  <Text size="xs" color="dimmed">
                    Domain: {violation.domain} |
                    {violation.blockingAction && ' BLOCKING ACTION'} |
                    Created: {new Date(violation.createdAt * 1000).toLocaleString()}
                  </Text>
                  {violation.recommendation && (
                    <Text size="sm" mt="xs" style={{ fontStyle: 'italic' }}>
                      Recommendation: {violation.recommendation}
                    </Text>
                  )}
                </Alert>
              ))}
            </Stack>
          </Card>
        )}

        {/* Real-time Safety Events */}
        {emergencyEventsData?.safetyEventCreated && (
          <Card>
            <Title order={4} mb="md">Recent Safety Events</Title>
            <ScrollArea h={200}>
              <Stack gap="xs">
                <Group gap="xs">
                  <Badge color="blue" size="sm">LIVE</Badge>
                  <Text size="sm">
                    Event: {emergencyEventsData.safetyEventCreated.eventType}
                  </Text>
                  <Text size="xs" color="dimmed">
                    Severity: {emergencyEventsData.safetyEventCreated.severity}
                  </Text>
                </Group>
              </Stack>
            </ScrollArea>
          </Card>
        )}
      </Stack>
    );
  };

  // Emergency halt handler for life-critical situations
  const handleEmergencyHalt = async () => {
    if (!selectedExecution || !currentTenant?.tenantId) return;

    try {
      await emergencyHaltSaga({
        variables: {
          input: {
            executionId: selectedExecution,
            reason: "Safety score below critical threshold",
            operator: "System Safety Monitor",
            emergencyAuthorization: "AUTO_SAFETY_HALT"
          }
        }
      });

      await logUserAction({
        action: 'EMERGENCY_HALT_SAGA',
        details: {
          executionId: selectedExecution,
          reason: 'Safety score below critical threshold',
          automaticHalt: true
        },
        metadata: {
          tenantId: currentTenant?.tenantId
        }
      });

      notifications.show({
        title: 'Emergency Halt Initiated',
        message: 'SAGA execution has been halted due to safety violation',
        color: 'red'
      });

      // Refresh data after emergency halt
      refetchExecutions();
      // refetchSafetyScores(); // DISABLED: Backend resolver not implemented yet
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to initiate emergency halt';
      notifications.show({
        title: 'Emergency Halt Failed',
        message: errorMessage,
        color: 'red'
      });
    }
  };

  // LIVE EVENTS: Real-time event stream visualization
  const renderLiveEvents = () => {
    // Combine all event streams into a unified timeline
    const allEvents = [];

    // Add execution events
    if (executionEventsData?.sagaStateTransitions) {
      allEvents.push({
        type: 'EXECUTION',
        ...executionEventsData.sagaStateTransitions,
        sagaId: executionEventsData.sagaStateTransitions.sagaExecutionId,
        executionId: executionEventsData.sagaStateTransitions.sagaExecutionId,
        status: executionEventsData.sagaStateTransitions.toState,
        currentStep: executionEventsData.sagaStateTransitions.stepId,
        displayTime: new Date(executionEventsData.sagaStateTransitions.timestamp).toLocaleTimeString()
      });
    }

    // Add step events
    if (stepEventsData?.sagaStepEvents) {
      allEvents.push({
        type: 'STEP',
        ...stepEventsData.sagaStepEvents,
        displayTime: new Date(stepEventsData.sagaStepEvents.timestamp).toLocaleTimeString()
      });
    }

    // Add safety events
    if (emergencyEventsData?.safetyEventCreated) {
      allEvents.push({
        type: 'SAFETY',
        ...emergencyEventsData.safetyEventCreated,
        displayTime: new Date(emergencyEventsData.safetyEventCreated.timestamp).toLocaleTimeString()
      });
    }

    // Add emergency events
    if (emergencyEventsData?.safetyEventCreated) {
      allEvents.push({
        type: 'EMERGENCY',
        ...emergencyEventsData.safetyEventCreated,
        eventType: emergencyEventsData.safetyEventCreated.eventType,
        reason: emergencyEventsData.safetyEventCreated.description,
        operator: emergencyEventsData.safetyEventCreated.userId,
        displayTime: new Date(emergencyEventsData.safetyEventCreated.timestamp).toLocaleTimeString()
      });
    }

    // Add coordination events
    if (coordinationEventsData?.sagaStateTransitions) {
      allEvents.push({
        type: 'COORDINATION',
        ...coordinationEventsData.sagaStateTransitions,
        coordinatorId: coordinationEventsData.sagaStateTransitions.userId,
        executionId: coordinationEventsData.sagaStateTransitions.sagaExecutionId,
        lockId: coordinationEventsData.sagaStateTransitions.eventId,
        displayTime: new Date(coordinationEventsData.sagaStateTransitions.timestamp).toLocaleTimeString()
      });
    }

    // Sort events by timestamp (most recent first)
    allEvents.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    return (
      <Stack>
        {/* Live Event Stream Header */}
        <Card>
          <Group justify="space-between" mb="md">
            <Title order={4}>Real-Time Event Stream</Title>
            <Group gap="xs">
              <Badge color="green" variant="dot" size="lg">
                LIVE MONITORING
              </Badge>
              <Text size="sm" color="dimmed">
                {allEvents.length} recent events
              </Text>
            </Group>
          </Group>

          <Group gap="md" mb="md">
            <Badge color="blue" size="sm">Execution Events</Badge>
            <Badge color="purple" size="sm">Step Events</Badge>
            <Badge color="red" size="sm">Safety Events</Badge>
            <Badge color="orange" size="sm">Emergency Events</Badge>
            <Badge color="gray" size="sm">Coordination Events</Badge>
          </Group>
        </Card>

        {/* Real-Time Event Timeline */}
        <Card>
          <ScrollArea h={500}>
            <Timeline>
              {allEvents.map((event, index) => (
                <Timeline.Item
                  key={`${event.type}-${index}`}
                  bullet={getEventIcon(event.type)}
                  title={getEventTitle(event)}
                  color={getEventColor(event)}
                >
                  <Group gap="xs" mb="xs">
                    <Badge
                      color={getEventColor(event)}
                      size="xs"
                    >
                      {event.type}
                    </Badge>
                    <Text size="xs" color="dimmed">
                      {event.displayTime}
                    </Text>
                  </Group>

                  <Stack gap="xs">
                    {/* Event-specific details */}
                    {event.type === 'EXECUTION' && (
                      <Text size="sm">
                        SAGA {event.sagaId} • Status: {event.status} • Step: {event.currentStep || 'N/A'}
                      </Text>
                    )}

                    {event.type === 'STEP' && (
                      <Text size="sm">
                        Step: {event.stepName} ({event.primitiveType}) • Status: {event.status}
                        {event.duration && ` • Duration: ${event.duration}ms`}
                      </Text>
                    )}

                    {event.type === 'SAFETY' && (
                      <Text size="sm">
                        Safety Event: {event.eventType} • Score: {event.safetyScore?.overall?.toFixed(1)}
                        {event.safetyScore?.violations?.length > 0 &&
                          ` • ${event.safetyScore.violations.length} violations`
                        }
                      </Text>
                    )}

                    {event.type === 'EMERGENCY' && (
                      <Text size="sm" color="red">
                        🚨 {event.eventType}: {event.reason} • Operator: {event.operator}
                      </Text>
                    )}

                    {event.type === 'COORDINATION' && (
                      <Text size="sm">
                        Coordinator: {event.coordinatorId} • Event: {event.eventType}
                        {event.lockId && ` • Lock: ${event.lockId}`}
                      </Text>
                    )}

                    {/* Show execution ID for context */}
                    {event.executionId && (
                      <Text size="xs" color="dimmed">
                        Execution: {event.executionId}
                      </Text>
                    )}

                    {/* Show metadata if available */}
                    {event.metadata && Object.keys(event.metadata).length > 0 && (
                      <details>
                        <summary style={{ fontSize: '12px', color: 'gray', cursor: 'pointer' }}>
                          Show metadata
                        </summary>
                        <JsonInput
                          value={JSON.stringify(event.metadata, null, 2)}
                          readOnly
                          autosize
                          minRows={2}
                          maxRows={6}
                          size="xs"
                        />
                      </details>
                    )}
                  </Stack>
                </Timeline.Item>
              ))}

              {allEvents.length === 0 && (
                <Timeline.Item bullet={<IconClock size={16} />} title="No Events">
                  <Text size="sm" color="dimmed">
                    Waiting for real-time events... WebSocket connections active.
                  </Text>
                </Timeline.Item>
              )}
            </Timeline>
          </ScrollArea>
        </Card>
      </Stack>
    );
  };

  // Helper functions for event visualization
  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'EXECUTION': return <IconTimeline size={16} />;
      case 'STEP': return <IconActivity size={16} />;
      case 'SAFETY': return <IconShield size={16} />;
      case 'EMERGENCY': return <IconAlertTriangle size={16} />;
      case 'COORDINATION': return <IconDatabase size={16} />;
      default: return <IconClock size={16} />;
    }
  };

  // Proper TypeScript interface for event data
  interface EventData {
    type: 'EXECUTION' | 'STEP' | 'SAFETY' | 'EMERGENCY' | 'COORDINATION';
    status?: string;
    safetyScore?: {
      overall: number;
    };
    [key: string]: unknown;
  }

  const getEventColor = (event: EventData) => {
    switch (event.type) {
      case 'EXECUTION':
        if (event.status === Status.FAILED) return 'red';
        if (event.status === Status.COMPENSATED) return 'orange';
        if (event.status === Status.COMPLETED) return 'green';
        return 'blue';
      case 'STEP':
        if (event.status === Status.FAILED) return 'red';
        if (event.status === Status.COMPLETED) return 'green';
        return 'purple';
      case 'SAFETY':
        if (typeof event.safetyScore?.overall === 'number' && event.safetyScore.overall < 70) return 'red';
        if (typeof event.safetyScore?.overall === 'number' && event.safetyScore.overall < 85) return 'yellow';
        return 'green';
      case 'EMERGENCY': return 'red';
      case 'COORDINATION': return 'gray';
      default: return 'blue';
    }
  };

  const getEventTitle = (event: EventData) => {
    switch (event.type) {
      case 'EXECUTION': return `SAGA Execution ${event.eventType || 'Event'}`;
      case 'STEP': return `Step ${event.eventType || 'Event'}`;
      case 'SAFETY': return `Safety ${event.eventType || 'Assessment'}`;
      case 'EMERGENCY': return `Emergency: ${event.eventType}`;
      case 'COORDINATION': return `Coordination ${event.eventType}`;
      default: return 'System Event';
    }
  };

  // Handle loading and error states
  if (executionsLoading || definitionsLoading) {
    return <LoadingOverlay />;
  }

  if (executionsError) {
    return (
      <Alert color="red" icon={<IconAlertTriangle />}>
        Failed to load SAGA executions: {executionsError.message}
      </Alert>
    );
  }

  return (
    <ErrorBoundary>
      <Container size="xl">
        <Group justify="space-between" mb="lg">
          <Title order={2}>SAGA Coordination Monitor</Title>
          <Group>
            <Badge color="blue">{currentTenant?.tenantId || 'Unknown Tenant'}</Badge>
            <Badge color="green">Live</Badge>
          </Group>
        </Group>

        <Tabs value={activeTab} onChange={(value: string | null) => setActiveTab(value ?? 'overview')}>
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconActivity size={16} />}>
              Overview
            </Tabs.Tab>
            <Tabs.Tab value="executions" leftSection={<IconTimeline size={16} />}>
              Executions
            </Tabs.Tab>
            <Tabs.Tab value="definitions" leftSection={<IconDatabase size={16} />}>
              Definitions
            </Tabs.Tab>
            <Tabs.Tab value="safety" leftSection={<IconShield size={16} />}>
              <Text c={selectedExecution && safetyScoresData?.sagaSafetyScores?.overall < 70 ? 'red' : undefined}>
                Safety Monitoring
              </Text>
            </Tabs.Tab>
            <Tabs.Tab value="events" leftSection={<IconActivity size={16} />}>
              <Group gap="xs">
                <Text>Live Events</Text>
                <Badge color="green" size="sm" variant="dot">LIVE</Badge>
              </Group>
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview" pt="md">
            {renderOverview()}
          </Tabs.Panel>

          <Tabs.Panel value="executions" pt="md">
            {renderExecutions()}
          </Tabs.Panel>

          <Tabs.Panel value="definitions" pt="md">
            {renderDefinitions()}
          </Tabs.Panel>

          <Tabs.Panel value="safety" pt="md">
            {renderSafetyMonitoring()}
          </Tabs.Panel>

          <Tabs.Panel value="events" pt="md">
            {renderLiveEvents()}
          </Tabs.Panel>
        </Tabs>

        {/* Compensation Modal (Life-Critical Operation) */}
        <Modal
          opened={showCompensationModal}
          onClose={() => setShowCompensationModal(false)}
          title="LIFE-CRITICAL: Initiate SAGA Compensation"
          size="md"
        >
          <Alert color="orange" icon={<IconAlertTriangle />} mb="md">
            You are about to initiate compensation for a running SAGA. This is a
            life-critical operation that will trigger distributed rollback across
            all workflow steps.
          </Alert>

          <Stack>
            <TextInput
              label="SAGA Execution ID"
              value={selectedExecution || ''}
              readOnly
              description="The SAGA execution to compensate"
            />

            <TextInput
              label="Compensation Reason"
              placeholder="Enter reason for compensation..."
              value={compensationReason}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompensationReason(e.target.value)}
              required
              description="This will be logged in the immutable audit trail"
            />

            <Group justify="flex-end">
              <Button
                variant="outline"
                onClick={() => setShowCompensationModal(false)}
              >
                Cancel
              </Button>
              <Button
                color="orange"
                onClick={handleCompensation}
                disabled={!compensationReason.trim()}
              >
                Initiate Compensation
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Life-Critical Alerts */}
        {metrics.failedExecutions > 0 && (
          <Alert
            color="red"
            icon={<IconAlertTriangle />}
            style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000 }}
          >
            LIFE-CRITICAL: {metrics.failedExecutions} failed SAGA execution(s) detected
          </Alert>
        )}
      </Container>
    </ErrorBoundary>
  );
};